// Shared sort + format logic for name columns.
// Used by both the instructor cubits (to order rows) and the web UI (to render
// name cells), so the two never drift apart.

export type NameSortMode = 'first' | 'last'

export type NameParts = { first?: string | null; last?: string | null }

/**
 * Format a name for display.
 * - `'first'` mode → `"First Last"` (mirrors `PublicUser.fullName`).
 * - `'last'` mode → `"Last, First"`.
 *
 * Degrades gracefully: missing parts never produce a trailing comma, a leading
 * comma, or the literal `"undefined"`. A single-token name renders as that token
 * in either mode. Both parts empty → `""`.
 */
export function formatName(parts: NameParts, mode: NameSortMode): string {
  const first = (parts.first ?? '').trim()
  const last = (parts.last ?? '').trim()

  if (!first && !last) return ''

  if (mode === 'last') {
    if (!last) return first
    if (!first) return last
    return `${last}, ${first}`
  }

  return [first, last].filter(Boolean).join(' ')
}

/**
 * Compare two names for sorting (ascending / A–Z).
 *
 * Primary key is the chosen field (last name in `'last'` mode, first name in
 * `'first'` mode); the other field is the tiebreaker. Comparison is locale-aware
 * via `localeCompare`. Returns a negative/zero/positive number like any
 * `Array.prototype.sort` comparator; multiply by a direction modifier for `desc`.
 */
export function compareNames(
  a: NameParts,
  b: NameParts,
  mode: NameSortMode
): number {
  const aFirst = (a.first ?? '').trim()
  const bFirst = (b.first ?? '').trim()
  const aLast = (a.last ?? '').trim()
  const bLast = (b.last ?? '').trim()

  const [aPrimary, bPrimary, aSecondary, bSecondary] =
    mode === 'last'
      ? [aLast, bLast, aFirst, bFirst]
      : [aFirst, bFirst, aLast, bLast]

  const primary = aPrimary.localeCompare(bPrimary)
  if (primary !== 0) return primary

  return aSecondary.localeCompare(bSecondary)
}

/**
 * Return a new array sorted by name; never mutates the input.
 *
 * Primary sort key is the chosen field (last name in `'last'` mode, first name
 * in `'first'` mode); the other field is the tiebreaker. Comparison is
 * locale-aware via `localeCompare`. `order` `'asc'` is A–Z, `'desc'` is Z–A.
 */
export function sortByName<T>(
  rows: readonly T[],
  mode: NameSortMode,
  getName: (row: T) => NameParts,
  order: 'asc' | 'desc' = 'asc'
): T[] {
  const direction = order === 'asc' ? 1 : -1

  return rows
    .slice()
    .sort((a, b) => compareNames(getName(a), getName(b), mode) * direction)
}
